<template>
  <v-dialog
    v-model="isOpen"
    max-width="560"
    scrollable
    aria-label="Grocery list for this week"
  >
    <v-card>
      <v-card-title>Grocery list for this week</v-card-title>
      <v-card-text>
        <p v-if="!lines.length && !unlisted.length">
          Plan some meals first, then their ingredients show up here.
        </p>
        <template v-if="lines.length">
          <p class="mb-2">Untick what you already have.</p>
          <v-list density="compact">
            <v-list-item
              v-for="line in lines"
              :key="line.key"
              :title="line.text"
              :subtitle="details(line)"
            >
              <template v-slot:prepend>
                <v-list-item-action start>
                  <v-checkbox-btn
                    :aria-label="line.text"
                    :model-value="selected.has(line.key)"
                    @update:model-value="toggle(line.key)"
                  />
                </v-list-item-action>
              </template>
            </v-list-item>
          </v-list>
        </template>
        <template v-if="unlisted.length">
          <v-list-subheader
            >Not from a recipe: add these yourself</v-list-subheader
          >
          <v-list density="compact">
            <v-list-item
              v-for="meal in unlisted"
              :key="`${meal.day}-${meal.slot}`"
              :title="meal.title"
              :subtitle="`${capitalize(meal.day)} ${meal.slot}`"
              prepend-icon="mdi-pencil"
            />
          </v-list>
        </template>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="isOpen = false">Cancel</v-btn>
        <v-btn
          color="primary"
          variant="flat"
          :disabled="!selected.size"
          :loading="adding"
          @click="add"
        >
          Add {{ selected.size }} {{ selected.size === 1 ? 'item' : 'items' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
  import { defineComponent, type PropType, ref, watch } from 'vue';
  import type {
    ShoppingLine,
    UnlistedMeal,
  } from '@/service/weekplan/shoppingList';
  import { formatQuantity, type UnitSystem } from '@/service/recipe/units';

  export default defineComponent({
    name: 'ShoppingListDialog',
    props: {
      unitSystem: {
        type: String as PropType<UnitSystem>,
        default: 'original',
      },
      open: {
        type: Boolean,
        default: false,
      },
      lines: {
        type: Array as PropType<ShoppingLine[]>,
        default: () => [],
      },
      unlisted: {
        type: Array as PropType<UnlistedMeal[]>,
        default: () => [],
      },
      adding: {
        type: Boolean,
        default: false,
      },
    },
    emits: {
      add: (_lines: ShoppingLine[]) => true,
      close: () => true,
    },
    setup(props, { emit }) {
      const isOpen = ref(props.open);
      // Everything is ticked; the user unticks what they already have
      const selected = ref(new Set<string>());

      watch(
        () => props.open,
        (open) => {
          isOpen.value = open;
          if (open) {
            selected.value = new Set(props.lines.map(({ key }) => key));
          }
        },
        { immediate: true },
      );
      watch(isOpen, (open) => {
        if (!open) emit('close');
      });

      return {
        isOpen,
        selected,
        toggle(key: string) {
          const next = new Set(selected.value);
          if (!next.delete(key)) next.add(key);
          selected.value = next;
        },
        // "450 Gram · Curry, Soup"
        details({ amount, unit, meals }: ShoppingLine): string {
          const quantity = formatQuantity(amount, unit, props.unitSystem);
          return [quantity, meals.join(', ')].filter(Boolean).join(' · ');
        },
        capitalize(text: string): string {
          return text.charAt(0).toUpperCase() + text.slice(1);
        },
        add() {
          emit(
            'add',
            props.lines.filter(({ key }) => selected.value.has(key)),
          );
        },
      };
    },
  });
</script>
